import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { API_BASE_URL } from '@/constants/app'
import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/store/auth.store'
import { tokenStorage } from '@/services/auth/token-storage'
import { getLastActivity, useLockStore, waitForUnlock } from '@/modules/auth/lock/idle-lock'
import { requestStepUp } from '@/modules/auth/lock/step-up'

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean; _stepUp?: boolean; _lockReplays?: number }

/** A request hit by the lock waits for unlock and is replayed — at most this often. */
const MAX_LOCK_REPLAYS = 3

let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = tokenStorage
      .refreshAccessToken()
      .then((tokens) => Boolean(tokens?.accessToken))
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

/**
 * Central Axios instance. Modules must not import Axios directly —
 * use `services/api/http.ts` helpers or module services.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: false,
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      // Last real input (any tab) — the server's idle lock uses it.
      config.headers['X-Last-Activity'] = String(getLastActivity())
    }
    // Default instance Content-Type is application/json. FormData uploads must
    // let the browser set multipart/form-data + boundary — otherwise multer
    // never sees `req.file` ("No audio file provided").
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type')
      } else {
        delete (config.headers as Record<string, unknown>)['Content-Type']
      }
    }
    return config
  },
  (error: unknown) => Promise.reject(error),
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status
    const original = error.config as RetryConfig | undefined
    const url = original?.url ?? ''
    const isAuthRefresh = url.includes('/auth/refresh')
    // Sign-in calls answer 401 for wrong credentials — never a refresh case.
    const isAuthLogin = ['/auth/login', '/auth/pin-login', '/auth/device/forget', '/auth/unlock', '/auth/forgot-password'].some((path) =>
      url.includes(path),
    )

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !isAuthRefresh &&
      !isAuthLogin
    ) {
      original._retry = true
      const ok = await tryRefresh()
      if (ok) {
        const access = tokenStorage.getAccessToken()
        if (access) {
          original.headers.Authorization = `Bearer ${access}`
        }
        return apiClient(original)
      }

      useAuthStore.getState().clearSession()
      if (window.location.pathname !== ROUTES.LOGIN) {
        const returnTo = `${window.location.pathname}${window.location.search}`
        const loginUrl =
          returnTo && returnTo !== ROUTES.LOGIN
            ? `${ROUTES.LOGIN}?returnTo=${encodeURIComponent(returnTo)}`
            : ROUTES.LOGIN
        window.location.assign(loginUrl)
      }
    }

    const code = (error.response?.data as { code?: string } | undefined)?.code
    // PIN session + sensitive action: confirm password, then replay the request.
    if (status === 403 && code === 'STEP_UP_REQUIRED' && original && !original._stepUp) {
      original._stepUp = true
      if (await requestStepUp()) return apiClient(original)
    }
    if (status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') {
      useAuthStore.getState().updateUser({ mustChangePassword: true })
    }
    if (status === 403 && code === 'PIN_SETUP_REQUIRED') {
      const security = useAuthStore.getState().session?.user.security
      if (security) {
        useAuthStore.getState().updateUser({ security: { ...security, pinSet: false, pinSetupRequired: true } })
      }
    }
    if (status === 423 && code === 'SESSION_LOCKED') {
      useLockStore.getState().lock()
      // Hold the call until the PIN is entered, then replay it — pages keep
      // loading instead of landing on "Retry" (signed out meanwhile → reject).
      if (original && (original._lockReplays ?? 0) < MAX_LOCK_REPLAYS) {
        original._lockReplays = (original._lockReplays ?? 0) + 1
        await waitForUnlock()
        if (useAuthStore.getState().session) {
          const access = tokenStorage.getAccessToken()
          if (access) original.headers.Authorization = `Bearer ${access}`
          return apiClient(original)
        }
      }
    }

    return Promise.reject(error)
  },
)

export default apiClient
